import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Match, Player, Team, BatsmanStats, BowlerStats, Wicket, ScoreComponents, ScoreEntry } from '../../../types/cricket';
import { toast } from '../../../components/ui/use-toast';
import { updateMatchInnings, createWicket, fetchWickets, deleteWicket } from '../../../services/supabaseService';
import { saveBattingStats, saveBowlingStats, loadBattingStats, loadBowlingStats, saveScoreHistory, loadScoreHistory, deleteScoreHistoryEntry } from '../../../services/matchStatsService';
import { MatchStatus } from '../../../components/match/MatchStatus';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { v4 as uuidv4 } from 'uuid';
import MatchCompletionDialog from '../../../components/match/MatchCompletionDialog';

interface MobileLiveScoreboardProps {
  match: Match;
  teams: Team[];
  onScoreUpdate: (matchId: string, teamId: string, runs: number, wickets: number, overs: number, balls: number) => void;
  onMatchStatusChange: (matchId: string, status: 'upcoming' | 'live' | 'completed') => void;
}

export const MobileLiveScoreboard: React.FC<MobileLiveScoreboardProps> = ({
  match,
  teams,
  onScoreUpdate,
  onMatchStatusChange
}) => {
  const { orientation } = useMobileDetection();
  const [currentScore, setCurrentScore] = useState({ runs: 0, wickets: 0, overs: 0, balls: 0 });
  const [selectedRuns, setSelectedRuns] = useState(0);
  const [selectedExtras, setSelectedExtras] = useState(0);
  const [extraType, setExtraType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWicketDialog, setShowWicketDialog] = useState(false);
  const [wicketType, setWicketType] = useState('');
  const [currentStriker, setCurrentStriker] = useState('');
  const [currentNonStriker, setCurrentNonStriker] = useState('');
  const [currentBowler, setCurrentBowler] = useState('');
  const [scoreHistory, setScoreHistory] = useState<ScoreEntry[]>([]);

  // Mobile-specific state
  const [activeTab, setActiveTab] = useState('scoring');
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showMatchCompletionDialog, setShowMatchCompletionDialog] = useState(false);
  const [pendingMatchCompletionReason, setPendingMatchCompletionReason] = useState<string>('');

  const battingTeam = teams.find(t => t.id === match.currentBattingTeam);
  const bowlingTeam = teams.find(t => t.id !== match.currentBattingTeam);

  // Add state for wickets
  const [wickets, setWickets] = useState<Wicket[]>([]);

  // Create a list that includes all players but marks dismissed ones as inactive
  const allBatsmenWithStatus = battingTeam?.players?.map(p => {
    // Check if player is dismissed by looking at the wickets table
    const isOut = wickets.some(w => w.dismissedBatterId === p.id);
    return {
      ...p,
      isOut: !!isOut
    };
  }) || [];

  // Available batsmen (only those not dismissed)
  const availableBatsmen = allBatsmenWithStatus.filter(p => !p.isOut);
  const availableBowlers = bowlingTeam?.players || [];

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const history = await loadScoreHistory(match.id);
        const wicketsData = await fetchWickets(match.id);
        setScoreHistory(history);
        setWickets(wicketsData);
        
        // Set current score from match data
        const currentBattingTeamScore = match.currentBattingTeam === match.team1Id ? match.team1Score : match.team2Score;
        setCurrentScore({
          runs: currentBattingTeamScore.runs || 0,
          wickets: currentBattingTeamScore.wickets || 0,
          overs: currentBattingTeamScore.overs || 0,
          balls: currentBattingTeamScore.balls || 0
        });
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadInitialData();
  }, [match.id, match.innings, match.currentInnings]);

  const handleAddScore = async () => {
    if (!currentStriker || !currentBowler) {
      toast({
        title: "Missing Information",
        description: "Please select striker and bowler",
        variant: "destructive",
      });
      return;
    }

    // Check if selected players are dismissed
    const strikerIsOut = allBatsmenWithStatus.find(p => p.id === currentStriker)?.isOut;
    const nonStrikerIsOut = allBatsmenWithStatus.find(p => p.id === currentNonStriker)?.isOut;
    
    if (strikerIsOut) {
      toast({
        title: "Validation Error",
        description: "Cannot select a dismissed player as striker.",
        variant: "destructive",
      });
      return;
    }
    
    if (nonStrikerIsOut) {
      toast({
        title: "Validation Error",
        description: "Cannot select a dismissed player as non-striker.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const newRuns = currentScore.runs + selectedRuns + selectedExtras;
      const newBalls = currentScore.balls + 1;
      const newOvers = Math.floor(newBalls / 6);
      const remainingBalls = newBalls % 6;

      const updatedScore = {
        runs: newRuns,
        wickets: currentScore.wickets,
        overs: newOvers,
        balls: remainingBalls
      };

      setCurrentScore(updatedScore);
      onScoreUpdate(match.id, match.currentBattingTeam, newRuns, currentScore.wickets, newOvers, remainingBalls);

      // Save to history
      const description = `${selectedRuns + selectedExtras} run${selectedRuns + selectedExtras !== 1 ? 's' : ''}${extraType ? ` (${extraType})` : ''}`;
      const scoreEntry: ScoreEntry = {
        id: uuidv4(),
        runs: selectedRuns,
        extras: selectedExtras,
        extraType,
        ballCount: 1,
        bowlerRuns: 0,
        batsmanRuns: selectedRuns,
        timestamp: Date.now(),
        description,
        ballNumber: currentScore.balls,
        overNumber: currentScore.overs,
        innings: match.currentInnings,
        battingStats: [],
        bowlingStats: [],
        isFreeHit: false,
        bowlerId: currentBowler,
        strikerId: currentStriker,
        nonStrikerId: currentNonStriker,
        battingTeamId: match.currentBattingTeam
      };

      await saveScoreHistory(match.id, scoreEntry);
      
      // Check if match should be automatically completed
      const shouldCompleteMatch = () => {
        // If it's the second innings
        if (match.currentInnings === 2) {
          const previousScore = match.currentBattingTeam === match.team1Id ? match.team2Score : match.team1Score;
          const target = previousScore.runs + 1;
          
          // Check if target is achieved
          if (newRuns >= target) {
            return true;
          }
          
          // Check if overs completed or wickets completed
          if (newOvers >= (match.totalOvers || 20) || currentScore.wickets >= (match.totalWickets || 10)) {
            return true;
          }
        }
        
        return false;
      };

      if (shouldCompleteMatch()) {
        // Show confirmation dialog before completing match
        setShowMatchCompletionDialog(true);
        setPendingMatchCompletionReason('target_achieved');
        return; // Don't proceed with completion until confirmed
      }
      
      // Reset selections
      setSelectedRuns(0);
      setSelectedExtras(0);
      setExtraType('');

      toast({
        title: "Score Updated",
        description: `Added ${selectedRuns + selectedExtras} runs`,
      });
    } catch (error) {
      console.error('Error adding score:', error);
      toast({
        title: "Error",
        description: "Failed to update score",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleWicket = async () => {
    if (!wicketType) {
      toast({
        title: "Missing Information",
        description: "Please select wicket type",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const dismissedPlayerId = currentStriker; // or logic for run out
      if (!dismissedPlayerId || !currentBowler) {
        throw new Error('Missing required player information');
      }
      // Only use allowed wicket types
      const allowedWicketTypes = [
        'bowled','caught','lbw','stumped','run_out','hit_wicket','caught_and_bowled','caught_behind','obstructing','handled_ball','timed_out','retired_out'
      ];
      if (!allowedWicketTypes.includes(wicketType)) {
        throw new Error('Invalid wicket type');
      }

      // Check if review was used and result is "not_out"
      const isNotOutReview = false; // Mobile version doesn't have review UI yet
      
      // Calculate new balls (always count as a ball)
      const newBalls = currentScore.balls + 1;
      const newOvers = Math.floor(newBalls / 6); // Assuming 6 balls per over
      const remainingBalls = newBalls % 6;
      
      if (isNotOutReview) {
        // For "Not Out" reviews, don't create wicket record, just add to score history
        const scoreEntry: ScoreEntry = {
          id: uuidv4(),
          runs: 0,
          extras: 0,
          extraType: '',
          ballCount: 1,
          bowlerRuns: 0,
          batsmanRuns: 0,
          timestamp: Date.now(),
          description: `Review - ${wicketType} - ${allBatsmenWithStatus.find(p => p.id === dismissedPlayerId)?.name} - NOT OUT`,
          ballNumber: currentScore.balls,
          overNumber: currentScore.overs,
          innings: match.currentInnings,
          battingStats: [],
          bowlingStats: [],
          isFreeHit: false,
          bowlerId: currentBowler,
          strikerId: currentStriker,
          nonStrikerId: currentNonStriker,
          battingTeamId: match.currentBattingTeam
        };
        
        // Save to history
        await saveScoreHistory(match.id, scoreEntry);
        
        // Update match score (no wicket added)
        onScoreUpdate(match.id, match.currentBattingTeam, currentScore.runs, currentScore.wickets, newOvers, remainingBalls);
        
        // Update currentScore state
        setCurrentScore({ 
          ...currentScore, 
          overs: newOvers,
          balls: remainingBalls
        });
        
        toast({
          title: "Review - Not Out",
          description: `${wicketType} appeal - ${allBatsmenWithStatus.find(p => p.id === dismissedPlayerId)?.name} - NOT OUT`,
        });
      } else {
        // Normal wicket recording
        const wicket = {
          id: uuidv4(),
          matchId: match.id,
          playerId: dismissedPlayerId,
          bowlerId: currentBowler,
          wicketType,
          innings: match.currentInnings,
          overNumber: currentScore.overs,
          ballNumber: currentScore.balls,
          runsScored: currentScore.runs,
          dismissedBatterId: dismissedPlayerId,
          // userId is set by Supabase backend/session
        };
        
        await createWicket(wicket);

        // Refresh wickets data to update the UI
        const updatedWickets = await fetchWickets(match.id);
        setWickets(updatedWickets);

        // Clear dismissed player from current selections
        if (currentStriker === dismissedPlayerId) {
          setCurrentStriker('');
        }
        if (currentNonStriker === dismissedPlayerId) {
          setCurrentNonStriker('');
        }

        // Calculate new wickets
        const newWickets = currentScore.wickets + 1;
        
        // Create score entry for history
        const scoreEntry: ScoreEntry = {
          id: uuidv4(),
          runs: 0,
          extras: 0,
          extraType: '',
          ballCount: 1,
          bowlerRuns: 0,
          batsmanRuns: 0,
          wicket,
          timestamp: Date.now(),
          description: `Wicket - ${wicketType} - ${allBatsmenWithStatus.find(p => p.id === dismissedPlayerId)?.name}`,
          ballNumber: currentScore.balls,
          overNumber: currentScore.overs,
          innings: match.currentInnings,
          battingStats: [],
          bowlingStats: [],
          isFreeHit: false,
          bowlerId: currentBowler,
          strikerId: currentStriker,
          nonStrikerId: currentNonStriker,
          battingTeamId: match.currentBattingTeam
        };
        
        // Save to history
        await saveScoreHistory(match.id, scoreEntry);
        
        // Check if match should be automatically completed
        const shouldCompleteMatch = () => {
          // If it's the second innings
          if (match.currentInnings === 2) {
            const previousScore = match.currentBattingTeam === match.team1Id ? match.team2Score : match.team1Score;
            const target = previousScore.runs + 1;
            
            // Check if target is achieved (unlikely with wicket, but possible)
            if (currentScore.runs >= target) {
              return true;
            }
            
            // Check if wickets completed
            if (newWickets >= (match.totalWickets || 10)) {
              return true;
            }
          }
          
          return false;
        };

        if (shouldCompleteMatch()) {
          // Show confirmation dialog before completing match
          setShowMatchCompletionDialog(true);
          setPendingMatchCompletionReason('wickets_completed');
          return; // Don't proceed with completion until confirmed
        }
        
        // Update match score
        onScoreUpdate(match.id, match.currentBattingTeam, currentScore.runs, newWickets, newOvers, remainingBalls);
        
        // Update currentScore state
        setCurrentScore({ 
          ...currentScore, 
          wickets: newWickets,
          overs: newOvers,
          balls: remainingBalls
        });

        toast({
          title: "Wicket Recorded",
          description: `${wicketType} - ${allBatsmenWithStatus.find(p => p.id === dismissedPlayerId)?.name}`,
        });
      }

      setShowWicketDialog(false);
      setWicketType('');
    } catch (error) {
      console.error('Error recording wicket:', error);
      toast({
        title: "Error",
        description: "Failed to record wicket",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMatchCompletionConfirmed = async () => {
    try {
      // Complete the match
      onMatchStatusChange(match.id, 'completed');
      
      toast({
        title: "Match Completed",
        description: "Match has been completed successfully. All statistics are now finalized.",
      });
      
      // Close the dialog
      setShowMatchCompletionDialog(false);
      setPendingMatchCompletionReason('');
    } catch (error) {
      console.error('Error completing match:', error);
      toast({
        title: "Error",
        description: "Failed to complete match. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 pb-20">
      {/* Match Status */}
      <MatchStatus match={match} teams={teams} />

      {/* Main Score Display */}
      <Card className="bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-center text-lg">Live Score</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <div className="text-3xl font-bold text-foreground mb-2">
            {currentScore.runs}/{currentScore.wickets}
          </div>
          <div className="text-lg text-muted-foreground mb-2">
            {currentScore.overs}.{currentScore.balls} overs
          </div>
          <div className="text-sm text-muted-foreground">
            {battingTeam?.name} batting
          </div>
        </CardContent>
      </Card>

      {/* Mobile Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="scoring" className="text-xs">Scoring</TabsTrigger>
          <TabsTrigger value="players" className="text-xs">Players</TabsTrigger>
          <TabsTrigger value="history" className="text-xs">History</TabsTrigger>
        </TabsList>

        {/* Scoring Tab */}
        <TabsContent value="scoring" className="space-y-4">
          {/* Quick Run Buttons */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Add Runs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[0, 1, 2, 3, 4, 6].map((runs) => (
                  <Button
                    key={runs}
                    variant={selectedRuns === runs ? "default" : "outline"}
                    size="lg"
                    className="h-12 text-lg font-bold"
                    onClick={() => setSelectedRuns(runs)}
                  >
                    {runs}
                  </Button>
                ))}
              </div>

              {/* Extras */}
              <div className="space-y-2">
                <Label className="text-sm">Extras</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={extraType} onValueChange={setExtraType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select extra" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wide">Wide</SelectItem>
                      <SelectItem value="no-ball">No Ball</SelectItem>
                      <SelectItem value="bye">Bye</SelectItem>
                      <SelectItem value="leg-bye">Leg Bye</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Extra runs"
                    value={selectedExtras}
                    onChange={(e) => setSelectedExtras(Number(e.target.value))}
                    className="text-center"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button
                  onClick={handleAddScore}
                  disabled={isLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Add Score
                </Button>
                <Button
                  onClick={() => setShowWicketDialog(true)}
                  variant="destructive"
                  disabled={isLoading}
                >
                  Wicket
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Players Tab */}
        <TabsContent value="players" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Current Players</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Batsmen */}
              <div>
                <Label className="text-sm font-medium">Striker</Label>
                <Select value={currentStriker} onValueChange={setCurrentStriker}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select striker" />
                  </SelectTrigger>
                  <SelectContent>
                    {allBatsmenWithStatus
                      .sort((a, b) => {
                        // Sort: active players first, then dismissed players
                        if (a.isOut && !b.isOut) return 1;
                        if (!a.isOut && b.isOut) return -1;
                        return 0;
                      })
                      .map(player => (
                        <SelectItem 
                          key={player.id} 
                          value={player.id} 
                          disabled={player.isOut}
                          className={player.isOut ? "opacity-50 cursor-not-allowed" : ""}
                        >
                          <span className={player.isOut ? "line-through text-gray-500" : ""}>
                            {player.name}
                          </span>
                          {player.isOut && (
                            <span className="ml-2 text-xs text-red-500">(OUT)</span>
                          )}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">Non-Striker</Label>
                <Select value={currentNonStriker} onValueChange={setCurrentNonStriker}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select non-striker" />
                  </SelectTrigger>
                  <SelectContent>
                    {allBatsmenWithStatus
                      .sort((a, b) => {
                        // Sort: active players first, then dismissed players
                        if (a.isOut && !b.isOut) return 1;
                        if (!a.isOut && b.isOut) return -1;
                        return 0;
                      })
                      .map(player => (
                        <SelectItem 
                          key={player.id} 
                          value={player.id} 
                          disabled={player.isOut}
                          className={player.isOut ? "opacity-50 cursor-not-allowed" : ""}
                        >
                          <span className={player.isOut ? "line-through text-gray-500" : ""}>
                            {player.name}
                          </span>
                          {player.isOut && (
                            <span className="ml-2 text-xs text-red-500">(OUT)</span>
                          )}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">Bowler</Label>
                <Select value={currentBowler} onValueChange={setCurrentBowler}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select bowler" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBowlers.map(player => (
                      <SelectItem key={player.id} value={player.id}>
                        {player.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Score History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {scoreHistory.slice(-10).reverse().map((entry, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-muted rounded">
                    <span className="text-sm">{entry.description}</span>
                    <span className="text-xs text-muted-foreground">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Wicket Dialog */}
      <Dialog open={showWicketDialog} onOpenChange={setShowWicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Wicket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Wicket Type</Label>
              <Select value={wicketType} onValueChange={setWicketType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select wicket type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bowled">Bowled</SelectItem>
                  <SelectItem value="caught">Caught</SelectItem>
                  <SelectItem value="lbw">LBW</SelectItem>
                  <SelectItem value="run-out">Run Out</SelectItem>
                  <SelectItem value="stumped">Stumped</SelectItem>
                  <SelectItem value="hit-wicket">Hit Wicket</SelectItem>
                  <SelectItem value="obstructing">Obstructing the Field</SelectItem>
                  <SelectItem value="handled-ball">Handled the Ball</SelectItem>
                  <SelectItem value="timed-out">Timed Out</SelectItem>
                  <SelectItem value="retired-out">Retired Out</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleWicket} disabled={isLoading} className="flex-1">
                Record Wicket
              </Button>
              <Button variant="outline" onClick={() => setShowWicketDialog(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Match Completion Confirmation Dialog */}
      <MatchCompletionDialog
        trigger={<div style={{ display: 'none' }} />} // Hidden trigger, controlled by state
        open={showMatchCompletionDialog}
        onOpenChange={setShowMatchCompletionDialog}
        onConfirm={handleMatchCompletionConfirmed}
        onCancel={() => {
          setShowMatchCompletionDialog(false);
          setPendingMatchCompletionReason('');
        }}
        matchName={`${battingTeam?.name} vs ${bowlingTeam?.name}`}
        reason={pendingMatchCompletionReason}
      />
    </div>
  );
}; 